// Qgis2threejs.js
// (C) 2014 Minoru Akagi | MIT License
// https://github.com/minorua/Qgis2threejs
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

const Q3D = { VERSION: '2.3.1' };

Q3D.Config = {
  allVisible: true, // set every layer visible property to true on load if set to true
  autoZShift: false, // automatic z shift adjustment
  bgcolor: null, // null is sky
  camera: {
    ortho: false,
  },
  lights: [
    {
      type: 'ambient',
      color: 0x999999,
      intensity: 0.8,
    },
    {
      type: 'directional',
      color: 0xffffff,
      intensity: 0.7,
      azimuth: 220, // note: default light azimuth of gdaldem hillshade is 315.
      altitude: 45, // altitude angle
    },
    {
      type: 'directional',
      color: 0xffffff,
      intensity: 0.1,
      azimuth: 40,
      altitude: -45,
    },
  ],
  dem: {
    side: {
      color: 0xc7ac92,
      bottomZ: -1.5, // in world coordinates
    },
    frame: {
      color: 0,
      bottomZ: -1.5,
    },
  },
  line: {
    dash: {
      dashSize: 1,
      gapSize: 0.5,
    },
  },
  northArrow: {
    color: 0x8b4513,
    cameraDistance: 30,
    visible: false,
  },
  qmarker: {
    r: 0.25,
    c: 0xffff00,
    o: 0.8,
  },
  debugMode: false,
  exportMode: false,
};

// consts
Q3D.LayerType = {
  DEM: 'dem',
  Point: 'point',
  Line: 'line',
  Polygon: 'polygon',
};

Q3D.MaterialType = {
  MeshLambert: 0,
  MeshPhong: 1,
  MeshToon: 2,
  LineBasic: 3,
  LineDashed: 4,
  Sprite: 5,
  Point: 6,
  Unknown: -1,
};

Q3D.uv = {
  i: new THREE.Vector3(1, 0, 0),
  j: new THREE.Vector3(0, 1, 0),
  k: new THREE.Vector3(0, 0, 1),
};

Q3D.ua = window.navigator.userAgent.toLowerCase();
Q3D.isIE = (Q3D.ua.indexOf('msie') !== -1 || Q3D.ua.indexOf('trident') !== -1);
Q3D.isTouchDevice = ('ontouchstart' in window);

/*
Q3D.Group -> THREE.Group -> THREE.Object3D
*/
Q3D.Group = function Group() {
  THREE.Group.call(this);
};

Q3D.Group.prototype = Object.create(THREE.Group.prototype);
Q3D.Group.prototype.constructor = Q3D.Group;

Q3D.Group.prototype.add = function add(object) {
  THREE.Group.prototype.add.call(this, object);
  object.updateMatrixWorld();
};

Q3D.Group.prototype.clear = function clear() {
  for (let i = this.children.length - 1; i >= 0; i -= 1) {
    this.remove(this.children[i]);
  }
};


/*
Q3D.Scene -> THREE.Scene -> THREE.Object3D

.mapLayers: an object that holds map layers contained in this scene. the key is layerId.
            use .loadJSONObject() to add a map layer to this scene.
.userData: an object that holds metadata (crs, proj, baseExtent, rotation,
            width, zExaggeration, zShift, wgs84Center)
           properties of the scene object in JSON data?

.add(object):
.getObjectByName(layerId): returns the layer object specified by the layer id.

--
custom function
.loadJSONObject(json_obj):
.toMapCoordinates(x, y, z): converts world coordinates to map coordinates
._rotatePoint(point, degrees, origin):
*/
Q3D.Scene = function Scene() {
  THREE.Scene.call(this);
  this.autoUpdate = false;

  // scene is z-up
  this.rotation.x = -Math.PI / 2;
  this.updateMatrixWorld();

  this.mapLayers = {};

  this.lightGroup = new Q3D.Group();
  this.add(this.lightGroup);
};

Q3D.Scene.prototype = Object.create(THREE.Scene.prototype);
Q3D.Scene.prototype.constructor = Q3D.Scene;

Q3D.Scene.prototype.add = function add(object) {
  THREE.Scene.prototype.add.call(this, object);
  object.updateMatrixWorld();
};

Q3D.Scene.prototype.loadJSONObject = function loadJSONObject(jsonObject) {
  if (jsonObject.type === 'scene') {
    // set properties
    if (jsonObject.properties !== undefined) {
      this.userData = jsonObject.properties;

      const w = (this.userData.baseExtent[2] - this.userData.baseExtent[0]);


      const h = (this.userData.baseExtent[3] - this.userData.baseExtent[1]);

      this.userData.scale = this.userData.width / w;
      this.userData.zScale = this.userData.scale * this.userData.zExaggeration;

      this.userData.origin = {
        x: this.userData.baseExtent[0] + w / 2,
        y: this.userData.baseExtent[1] + h / 2,
        z: -this.userData.zShift,
      };
    }

    // load lights
    if (jsonObject.lights !== undefined) {
      // remove all existing lights
      this.lightGroup.clear();

      // build lights if scene data has lights settings
      // [not implemented yet]
    }

    // build default lights if this scene has no lights yet
    if (this.lightGroup.children.length === 0) this.buildDefaultLights();

    // load layers
    if (jsonObject.layers !== undefined) {
      jsonObject.layers.forEach((layer) => {
        this.loadJSONObject(layer);
      }, this);
    }
  } else if (jsonObject.type === 'layer') {
    let layer = this.mapLayers[jsonObject.id];
    if (layer === undefined) {
      // console.assert(jsonObject.properties !== undefined);

      // create a layer
      const { type } = jsonObject.properties;

      if (type === 'dem') layer = new Q3D.DEMLayer();
      else if (type === 'point') layer = new Q3D.PointLayer();
      else if (type === 'line') layer = new Q3D.LineLayer();
      else if (type === 'polygon') layer = new Q3D.PolygonLayer();
      else {
        // console.error("unknown layer type:" + type);
        return;
      }
      layer.id = jsonObject.id;
      layer.addEventListener('renderRequest', this.requestRender.bind(this));

      this.mapLayers[jsonObject.id] = layer;
      this.add(layer.objectGroup);
    }

    layer.loadJSONObject(jsonObject, this);

    this.requestRender();
  } else if (jsonObject.type === 'block') {
    const layer = this.mapLayers[jsonObject.layer];

    if (layer === undefined) {
      // console.error("layer not exists:" + jsonObject.layer);
      return;
    }
    layer.loadJSONObject(jsonObject, this);

    this.requestRender();
  }
};

Q3D.Scene.prototype.buildLights = function buildLights(lights) {
  let p; let light; let lambda; let phi; let x; let y; let
    z;
  const deg2rad = Math.PI / 180;
  for (let i = 0; i < lights.length; i += 1) {
    p = lights[i];
    if (p.type === 'ambient') {
      this.lightGroup.add(new THREE.AmbientLight(p.color, p.intensity));
    } else if (p.type === 'directional') {
      lambda = (90 - p.azimuth) * deg2rad;
      phi = p.altitude * deg2rad;

      x = Math.cos(phi) * Math.cos(lambda);
      y = Math.cos(phi) * Math.sin(lambda);
      z = Math.sin(phi);

      light = new THREE.DirectionalLight(p.color, p.intensity);
      light.position.set(x, y, z);
      this.lightGroup.add(light);
    }
  }
};

Q3D.Scene.prototype.buildDefaultLights = function buildDefaultLights() {
  this.buildLights(Q3D.Config.lights);
};

Q3D.Scene.prototype.requestRender = function requestRender() {
  this.dispatchEvent({ type: 'renderRequest' });
};

Q3D.Scene.prototype.queryableObjects = function queryableObjects() {
  let objs = [];

  Object.keys(this.mapLayers).forEach((id) => {
    objs = objs.concat(this.mapLayers[id].queryableObjects());
  });

  return objs;
};

Q3D.Scene.prototype.toMapCoordinates = function toMapCoordinates(x, y, z) {
  if (this.userData.rotation) {
    const pt = this._rotatePoint({ x, y }, this.userData.rotation);

    ({ x, y } = pt);
  }

  return {
    x: x / this.userData.scale + this.userData.origin.x,
    y: y / this.userData.scale + this.userData.origin.y,
    z: z / this.userData.zScale + this.userData.origin.z,
  };
};

// real (geodetic/projected) coordinates to 3D scene coordinates
Q3D.Scene.prototype.toLocalCoordinates = function toLocalCoordinates(x, y, z) {
  // project x and y coordinates from WGS84 (long, lat)
  let pt;

  x = (x - this.userData.origin.x) * this.userData.scale;
  y = (y - this.userData.origin.y) * this.userData.scale;
  z = (z - this.userData.origin.z) * this.userData.zScale;

  if (this.userData.rotation) {
    pt = this._rotatePoint({ x, y }, -this.userData.rotation);
    ({ x, y } = pt);
  }
  return { x, y, z };
};

// Rotate a point counter-clockwise around an origin
Q3D.Scene.prototype._rotatePoint = function _rotatePoint(point, degrees, origin) {
  const theta = degrees * Math.PI / 180;
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  let { x, y } = point;

  if (origin) {
    x -= origin.x;
    y -= origin.y;
  }

  let xd = x * c - y * s;
  let yd = x * s + y * c;

  if (origin) {
    xd += origin.x;
    yd += origin.y;
  }
  return { x: xd, y: yd };
};

Q3D.Scene.prototype.adjustZShift = function adjustZShift() {
  // initialize
  this.userData.zShiftA = 0;
  this.position.y = 0;
  this.updateMatrixWorld();

  const box = new THREE.Box3();
  Object.keys(this.mapLayers).forEach((id) => {
    if (this.mapLayers[id].visible) {
      box.union(this.mapLayers[id].boundingBox());
    }
  });

  // bbox zmin in map coordinates
  const zmin = (box.min.y === Infinity)
    ? 0 : (box.min.y / this.userData.zScale - this.userData.zShift);

  // shift scene so that bbox zmin becomes zero
  this.userData.zShiftA = -zmin;
  this.position.y = this.userData.zShiftA * this.userData.zScale;

  // keep positions of lights in world coordinates
  this.lightGroup.position.z = -this.position.y;

  this.updateMatrixWorld();

  this.userData.origin.z = -(this.userData.zShift + this.userData.zShiftA);

  this.dispatchEvent({ type: 'zShiftAdjusted', sceneData: this.userData });

  this.requestRender();
};


Q3D.Application = function Application() {
  this.vec3 = new THREE.Vector3();
  this.listeners = {};

  this.mouseDownPoint = new THREE.Vector2();
  this.mouseUpPoint = new THREE.Vector2();

  this.queryTargetPosition = new THREE.Vector3(); // y-up
};

Q3D.Application.loadFile = function loadFile(url, type, callback) {
  const loader = new THREE.FileLoader();

  loader.setResponseType(type);

  loader.load(url, callback, undefined, () => {
    // console.error(error);
  });
};

Q3D.Application.loadJSONFile = function loadJSONFile(scene, url, callback) {
  Q3D.Application.loadFile(url, 'json', (obj) => {
    scene.loadJSONObject(obj);

    if (callback) callback(obj);
  });
};

Q3D.Application.prototype = {

  constructor: Q3D.Application,

  dispatchEvent(event) {
    // if (Q3D.Config.debugMode) console.log(`about to dispatch ${event} event.`);

    const ls = this.listeners[event.type] || [];
    for (let i = 0; i < ls.length; i += 1) {
      ls[i](event);
    }
  },

  addEventListener(type, listener, prepend) {
    this.listeners[type] = this.listeners[type] || [];
    if (prepend) {
      this.listeners[type].unshift(listener);
    } else {
      this.listeners[type].push(listener);
    }
  },

  init(container) {
    this.container = container;
    this.running = false; // if true, animation loop is continued.

    this.width = container.clientWidth;
    this.height = container.clientHeight;

    const { bgcolor } = Q3D.Config;

    if (bgcolor === null) container.classList.add('sky');

    // WebGLRenderer
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(this.width, this.height);
    this.renderer.setClearColor(bgcolor || 0, (bgcolor === null) ? 0 : 1);
    this.container.appendChild(this.renderer.domElement);

    // camera
    this.buildCamera(Q3D.Config.camera.ortho);

    // scene
    this.scene = new Q3D.Scene();
    this.scene.addEventListener('renderRequest', () => {
      this.render();
    });

    const controls = new OrbitControls(this.camera, this.renderer.domElement);

    controls.enableKeys = false;

    // custom functions
    const offset = new THREE.Vector3();
    const spherical = new THREE.Spherical();

    controls.moveForward = function moveForward(delta) {
      offset.copy(controls.object.position).sub(controls.target);
      const targetDistance = offset.length() * Math.tan(
        (controls.object.fov / 2) * Math.PI / 180.0,
      );
      offset.y = 0;
      offset.normalize();
      offset.multiplyScalar(-2 * delta * targetDistance / this.renderer.domElement.clientHeight);

      controls.object.position.add(offset);
      controls.target.add(offset);
    };
    controls.cameraRotate = function cameraRotate(thetaDelta, phiDelta) {
      offset.copy(controls.target).sub(controls.object.position);
      spherical.setFromVector3(offset);

      spherical.theta += thetaDelta;
      spherical.phi -= phiDelta;

      // restrict theta/phi to be between desired limits
      spherical.theta = Math.max(
        controls.minAzimuthAngle, Math.min(controls.maxAzimuthAngle, spherical.theta),
      );
      spherical.phi = Math.max(
        controls.minPolarAngle, Math.min(controls.maxPolarAngle, spherical.phi),
      );
      spherical.makeSafe();

      offset.setFromSpherical(spherical);
      controls.target.copy(controls.object.position).add(offset);
      controls.object.lookAt(controls.target);
    };

    controls.addEventListener('change', () => {
      this.render();
    });

    this.controls = controls;
    this.controls.update();

    // create a marker for queried point
    const opt = Q3D.Config.qmarker;
    this.queryMarker = new THREE.Mesh(new THREE.SphereBufferGeometry(opt.r),
      new THREE.MeshLambertMaterial({ color: opt.c, opacity: opt.o, transparent: (opt.o < 1) }));

    this.highlightMaterial = new THREE.MeshLambertMaterial({
      emissive: 0x999900, transparent: true, opacity: 0.5,
    });

    // Shader compilation error occurs with double sided material on IE11
    if (!Q3D.isIE) this.highlightMaterial.side = THREE.DoubleSide;

    this.selectedObject = null;
    this.highlightObject = null;

    this.modelBuilders = [];
    this._wireframeMode = false;

    window.addEventListener('keydown', this.keydown);
    window.addEventListener('resize', this.resize);

    this.renderer.domElement.addEventListener('mousedown', this.mousedown.bind(this));
    this.renderer.domElement.addEventListener('mouseup', this.mouseup.bind(this));
  },

  keydown(e) {
    const { controls } = this;
    const panDelta = 3; const
      rotateAngle = 2 * Math.PI / 180;
    if (e.shiftKey && e.ctrlKey) {
      switch (e.keyCode) {
        case 38: // Shift + Ctrl + UP
          controls.dollyOut(controls.getZoomScale());
          break;
        case 40: // Shift + Ctrl + DOWN
          controls.dollyIn(controls.getZoomScale());
          break;
        default:
          return;
      }
    } else if (e.shiftKey) {
      switch (e.keyCode) {
        case 37: // LEFT
          controls.rotateLeft(rotateAngle);
          break;
        case 38: // UP
          controls.rotateUp(rotateAngle);
          break;
        case 39: // RIGHT
          controls.rotateLeft(-rotateAngle);
          break;
        case 40: // DOWN
          controls.rotateUp(-rotateAngle);
          break;
        case 82: // Shift + R
          controls.reset();
          break;
        case 83: // Shift + S
          this.showPrintDialog();
          return;
        default:
          return;
      }
    } else if (e.ctrlKey) {
      switch (e.keyCode) {
        case 37: // Ctrl + LEFT
          controls.cameraRotate(rotateAngle, 0);
          break;
        case 38: // Ctrl + UP
          controls.cameraRotate(0, rotateAngle);
          break;
        case 39: // Ctrl + RIGHT
          controls.cameraRotate(-rotateAngle, 0);
          break;
        case 40: // Ctrl + DOWN
          controls.cameraRotate(0, -rotateAngle);
          break;
        default:
          return;
      }
    } else {
      switch (e.keyCode) {
        case 37: // LEFT
          controls.panLeft(panDelta, controls.object.matrix);
          break;
        case 38: // UP
          controls.moveForward(3 * panDelta); // horizontally forward
          break;
        case 39: // RIGHT
          controls.panLeft(-panDelta, controls.object.matrix);
          break;
        case 40: // DOWN
          controls.moveForward(-3 * panDelta);
          break;
        case 27: // ESC
          if (this.controls.autoRotate) {
            this.setRotateAnimationMode(false);
          }
          return;
        case 82: // R
          this.setRotateAnimationMode(!controls.autoRotate);
          return;
        case 87: // W
          this.setWireframeMode(!this._wireframeMode);
          return;
        default:
          return;
      }
    }
    this.controls.update();
  },

  mousedown(e) {
    this.mouseDownPoint.set(e.clientX, e.clientY);
  },

  mouseup(e) {
    this.mouseUpPoint.set(e.clientX, e.clientY);
    if (this.mouseDownPoint.equals(this.mouseUpPoint)) this.canvasClicked(e);
  },

  resize() {
    this.setCanvasSize(this.container.clientWidth, this.container.clientHeight);
    this.render();
  },

  setCanvasSize(width, height) {
    this.width = width;
    this.height = height;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  },

  buildCamera(isOrtho) {
    if (isOrtho) {
      this.camera = new THREE.OrthographicCamera(
        -this.width / 10, this.width / 10, this.height / 10, -this.height / 10, 0.1, 10000,
      );
    } else {
      this.camera = new THREE.PerspectiveCamera(45, this.width / this.height, 0.1, 10000);
    }
    this.camera.position.set(0, 100, 100);
  },

  // rotation: direction to North (clockwise from up (+y), in degrees)
  buildNorthArrow(container, rotation) {
    this.renderer2 = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer2.setClearColor(0, 0);
    this.renderer2.setSize(container.clientWidth, container.clientHeight);

    this.container2 = container;
    this.container2.appendChild(this.renderer2.domElement);

    this.camera2 = new THREE.PerspectiveCamera(
      45, container.clientWidth / container.clientHeight, 1, 1000,
    );
    this.camera2.up = this.camera.up;

    this.scene2 = new Q3D.Scene();
    this.scene2.buildDefaultLights();

    // an arrow object
    const geometry = new THREE.Geometry();
    geometry.vertices.push(
      new THREE.Vector3(-5, -10, 0),
      new THREE.Vector3(0, 10, 0),
      new THREE.Vector3(0, -7, 3),
      new THREE.Vector3(5, -10, 0),
    );
    geometry.faces.push(
      new THREE.Face3(0, 1, 2),
      new THREE.Face3(2, 1, 3),
    );
    geometry.computeFaceNormals();

    const material = new THREE.MeshLambertMaterial({
      color: Q3D.Config.northArrow.color,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.Mesh(geometry, material);

    if (rotation) mesh.rotation.z = -rotation * Math.PI / 180;

    this.scene2.add(mesh);
  },

  // enable the controls
  start() {
    if (this.controls) this.controls.enabled = true;
  },

  pause() {
    this.running = false;
    if (this.controls) this.controls.enabled = false;
  },

  resume() {
    if (this.controls) this.controls.enabled = true;
  },

  startAnimation() {
    this.running = true;
    this.animate();
  },

  stopAnimation() {
    this.running = false;
  },

  // animation loop
  animate() {
    if (this.running) requestAnimationFrame(this.animate);
    this.render(true);
  },

  render(updateControls) {
    if (updateControls) this.controls.update();
    this.renderer.render(this.scene, this.camera);

    // North arrow
    if (this.renderer2) {
      this.camera.getWorldDirection(this.vec3);
      this.camera2.position.copy(
        this.vec3.negate().setLength(Q3D.Config.northArrow.cameraDistance),
      );
      this.camera2.quaternion.copy(this.camera.quaternion);

      this.renderer2.render(this.scene2, this.camera2);
    }
  },

  setRotateAnimationMode(enabled) {
    this.controls.autoRotate = enabled;

    if (enabled) {
      this.startAnimation();
    } else {
      this.stopAnimation();
    }
  },

  setWireframeMode(wireframe) {
    if (wireframe === this._wireframeMode) return;

    Object.keys(this.scene.mapLayers).forEach((id) => {
      this.scene.mapLayers[id].setWireframeMode(wireframe);
    });

    this._wireframeMode = wireframe;
    this.render();
  },

  intersectObjects(offsetX, offsetY) {
    const vec2 = new THREE.Vector2((offsetX / this.width) * 2 - 1,
      -(offsetY / this.height) * 2 + 1);
    const ray = new THREE.Raycaster();
    ray.linePrecision = 0.2;
    ray.setFromCamera(vec2, this.camera);
    return ray.intersectObjects(this.scene.queryableObjects());
  },

  _offset(elm) {
    let top = 0; let
      left = 0;
    do {
      top += elm.offsetTop || 0; left += elm.offsetLeft || 0; elm = elm.offsetParent;
    } while (elm);
    return { top, left };
  },

  cameraAction: {

    move(x, y, z) { // z-up
      if (x === undefined) this.camera.position.copy(this.queryTargetPosition);
      else this.camera.position.set(x, z, -y); // y-up
      this.render(true);
    },

    vecZoom: new THREE.Vector3(0, 10, 10), // y-up

    zoomIn(x, y, z) { // z-up
      if (x === undefined) this.vec3.copy(this.queryTargetPosition);
      else this.vec3.set(x, z, -y); // y-up

      this.camera.position.addVectors(this.vec3, this.cameraAction.vecZoom);
      this.camera.lookAt(this.vec3.x, this.vec3.y, this.vec3.z);
      if (this.controls.target !== undefined) this.controls.target.copy(this.vec3);
      this.render(true);
    },

    orbit(x, y, z) { // z-up
      if (this.controls.target === undefined) return;

      if (x === undefined) this.controls.target.copy(this.queryTargetPosition);
      else this.controls.target.set(x, z, -y); // y-up
      this.setRotateAnimationMode(true);
    },

  },

  canvasClicked(e) {
    const canvasOffset = this._offset(this.renderer.domElement);
    const objs = this.intersectObjects(e.clientX - canvasOffset.left, e.clientY - canvasOffset.top);
    let obj; let
      pt;

    for (let i = 0, l = objs.length; i < l; i += 1) {
      obj = objs[i];

      // query marker
      pt = { x: obj.point.x, y: -obj.point.z, z: obj.point.y }; // obj's coordinate system is y-up
      this.queryMarker.position.set(pt.x, pt.y, pt.z); // this is z-up
      this.scene.add(this.queryMarker);

      // get layerId of clicked object
      let layerId;
      let { object } = obj;

      while (object) {
        ({ layerId } = object.userData);

        if (layerId !== undefined) break;

        object = object.parent;
      }

      this.render();

      return;
    }
  },
};


/*
Q3D.Material
*/
Q3D.Material = function Material() {
  this.loaded = false;
};

Q3D.Material.prototype = {

  constructor: Q3D.Material,

  // callback is called when material has been completely loaded
  loadJSONObject(jsonObject, callback) {
    this.origProp = jsonObject;

    const m = jsonObject; const opt = {}; let
      defer = false;

    if (m.ds && !Q3D.isIE) opt.side = THREE.DoubleSide;

    if (m.flat) opt.flatShading = true;

    // texture
    if (m.image !== undefined) {
      if (m.image.url !== undefined) {
        opt.map = new THREE.TextureLoader().load(m.image.url, () => {
          this._loadCompleted(callback);
        });
        defer = true;
      } else if (m.image.object !== undefined) { // WebKit Bridge
        opt.map = new THREE.Texture(m.image.object.toImageData());
        opt.map.needsUpdate = true;
      } else { // base64
        const img = new Image();
        img.onload = () => {
          opt.map.needsUpdate = true;
          this._loadCompleted(callback);
        };
        img.src = m.image.base64;
        opt.map = new THREE.Texture(img);
        defer = true;
      }
    }

    if (m.c !== undefined) opt.color = m.c;

    if (m.o !== undefined && m.o < 1) {
      opt.opacity = m.o;
      opt.transparent = true;
    }

    if (m.t) opt.transparent = true;

    if (m.w) opt.wireframe = true;

    if (m.type === Q3D.MaterialType.MeshLambert) {
      this.mtl = new THREE.MeshLambertMaterial(opt);
    } else if (m.type === Q3D.MaterialType.MeshPhong) {
      this.mtl = new THREE.MeshPhongMaterial(opt);
    } else if (m.type === Q3D.MaterialType.MeshToon) {
      this.mtl = new THREE.MeshToonMaterial(opt);
    } else if (m.type === Q3D.MaterialType.Point) {
      opt.size = m.s;
      this.mtl = new THREE.PointsMaterial(opt);
    } else if (m.type === Q3D.MaterialType.LineBasic) {
      this.mtl = new THREE.LineBasicMaterial(opt);
    } else if (m.type === Q3D.MaterialType.LineDashed) {
      opt.dashSize = Q3D.Config.line.dash.dashSize;
      opt.gapSize = Q3D.Config.line.dash.gapSize;
      this.mtl = new THREE.LineDashedMaterial(opt);
    } else {
      opt.color = 0xffffff;
      this.mtl = new THREE.SpriteMaterial(opt);
    }

    if (!defer) this._loadCompleted(callback);
  },

  _loadCompleted(anotherCallback) {
    this.loaded = true;

    if (this._callbacks !== undefined) {
      for (let i = 0; i < this._callbacks.length; i += 1) {
        this._callbacks[i]();
      }
      this._callbacks = [];
    }

    if (anotherCallback) anotherCallback();
  },

  callbackOnLoad(callback) {
    if (this.loaded) {
      callback();
      return;
    }

    if (this._callbacks === undefined) {
      this._callbacks = [];
    }

    this._callbacks.push(callback);
  },

  set(material) {
    this.mtl = material;
    this.origProp = {};
  },

  type() {
    if (this.mtl instanceof THREE.MeshLambertMaterial) return Q3D.MaterialType.MeshLambert;
    if (this.mtl instanceof THREE.MeshPhongMaterial) return Q3D.MaterialType.MeshPhong;
    if (this.mtl instanceof THREE.LineBasicMaterial) return Q3D.MaterialType.LineBasic;
    if (this.mtl instanceof THREE.LineDashedMaterial) return Q3D.MaterialType.LineDashed;
    if (this.mtl instanceof THREE.SpriteMaterial) return Q3D.MaterialType.Sprite;
    if (this.mtl === undefined) return undefined;
    if (this.mtl === null) return null;
    return Q3D.MaterialType.Unknown;
  },

  dispose() {
    if (!this.mtl) return;

    if (this.mtl.map) this.mtl.map.dispose(); // dispose of texture
    this.mtl.dispose();
    this.mtl = null;
  },
};

/*
Q3D.Materials
*/
Q3D.Materials = function Materials() {
  this.materials = [];
};

Q3D.Materials.prototype = Object.create(THREE.EventDispatcher.prototype);
Q3D.Materials.prototype.constructor = Q3D.Materials;

// material: instance of Q3D.Material object or THREE.Material-based object
Q3D.Materials.prototype.add = function add(material) {
  if (material instanceof Q3D.Material) this.materials.push(material);
  else {
    const mtl = new Q3D.Material();
    mtl.set(material);
    this.materials.push(mtl);
  }
};

Q3D.Materials.prototype.get = function get(index) {
  return this.materials[index];
};

Q3D.Materials.prototype.mtl = function mtl(index) {
  return this.materials[index].mtl;
};

Q3D.Materials.prototype.loadJSONObject = function loadJSONObject(jsonObject) {
  let iterated = false;
  const callback = () => {
    if (iterated) this.dispatchEvent({ type: 'renderRequest' });
  };

  for (let i = 0, l = jsonObject.length; i < l; i += 1) {
    const mtl = new Q3D.Material();
    mtl.loadJSONObject(jsonObject[i], callback);
    this.add(mtl);
  }
  iterated = true;
};

Q3D.Materials.prototype.dispose = function dispose() {
  for (let i = 0, l = this.materials.length; i < l; i += 1) {
    this.materials[i].dispose();
  }
  this.materials = [];
};

Q3D.Materials.prototype.addFromObject3D = function addFromObject3D(object) {
  const mtls = [];

  object.traverse((obj) => {
    if (obj.material === undefined) return;
    ((obj.material instanceof Array) ? obj.material : [obj.material]).forEach((mtl) => {
      if (mtls.indexOf(mtl) === -1) {
        mtls.push(mtl);
      }
    });
  });

  let material;
  for (let i = 0, l = mtls.length; i < l; i += 1) {
    material = new Q3D.Material();
    material.set(mtls[i]);
    this.materials.push(material);
  }
};

// opacity
Q3D.Materials.prototype.opacity = function opacity() {
  if (this.materials.length === 0) return 1;

  let sum = 0;
  for (let i = 0, l = this.materials.length; i < l; i += 1) {
    sum += this.materials[i].mtl.opacity;
  }
  return sum / this.materials.length;
};

Q3D.Materials.prototype.setOpacity = function setOpacity(opacity) {
  let material;
  for (let i = 0, l = this.materials.length; i < l; i += 1) {
    material = this.materials[i];
    material.mtl.transparent = Boolean(material.origProp.t) || (opacity < 1);
    material.mtl.opacity = opacity;
  }
};

// wireframe: boolean
Q3D.Materials.prototype.setWireframeMode = function setWireframeMode(wireframe) {
  let material;
  for (let i = 0, l = this.materials.length; i < l; i += 1) {
    material = this.materials[i];
    if (material.origProp.w || material.mtl instanceof THREE.LineBasicMaterial) continue;
    material.mtl.wireframe = wireframe;
  }
};


/*
Q3D.DEMBlock
*/
Q3D.DEMBlock = function DEMBlock() {};

Q3D.DEMBlock.prototype = {

  constructor: Q3D.DEMBlock,

  // obj: json object
  loadJSONObject(obj, layer, callback) {
    const { grid } = obj;
    this.data = obj;

    // load material
    this.material = new Q3D.Material();
    this.material.loadJSONObject(obj.material, () => {
      layer.requestRender();
    });
    layer.materials.add(this.material);

    // create a plane geometry
    let geom;
    if (layer.geometryCache) {
      const params = layer.geometryCache.parameters || {};
      if (params.width === obj.width && params.height === obj.height
          && params.widthSegments === grid.width - 1 && params.heightSegments === grid.height - 1) {
        geom = layer.geometryCache.clone();
        geom.parameters = layer.geometryCache.parameters;
      }
    }
    geom = geom || new THREE.PlaneBufferGeometry(
      obj.width, obj.height, grid.width - 1, grid.height - 1,
    );
    layer.geometryCache = geom;

    // create a mesh
    const mesh = new THREE.Mesh(geom, this.material.mtl);
    mesh.position.fromArray(obj.translate);
    mesh.scale.z = obj.zScale;

    const buildGeometry = function buildGeometry(gridValues) {
      const vertices = geom.attributes.position.array;
      for (let i = 0, j = 0, l = vertices.length; i < l; i += 1, j += 3) {
        vertices[j + 2] = gridValues[i];
      }
      geom.attributes.position.needsUpdate = true;

      // Calculate normals
      if (layer.properties.shading) {
        geom.computeVertexNormals();
      }

      if (callback) callback(mesh);
    };

    if (grid.url !== undefined) {
      Q3D.Application.loadFile(grid.url, 'arraybuffer', (buf) => {
        grid.array = new Float32Array(buf);
        buildGeometry(grid.array);
      });
    } else { // WebKit Bridge
      if (grid.binary !== undefined) {
        grid.array = new Float32Array(grid.binary.buffer, 0, grid.width * grid.height);
      }

      buildGeometry(grid.array);
    }

    this.obj = mesh;
    return mesh;
  },

  buildSides(layer, parent, z0) {
    const opacity = (this.material.origProp.o !== undefined) ? this.material.origProp.o : 1;
    const material = new THREE.MeshLambertMaterial({
      color: Q3D.Config.dem.side.color,
      opacity,
      transparent: (opacity < 1),
    });
    layer.materials.add(material);

    const planeWidth = this.data.width;
    const planeHeight = this.data.height;
    const { grid } = this.data;
    const gridValues = grid.array;
    const w = grid.width;
    const h = grid.height;
    const k = w * (h - 1);
    const e0 = z0 / this.data.zScale - this.data.zShift - (this.data.zShiftA || 0);
    const band_width = -2 * e0;

    // front and back
    const geom_fr = new THREE.PlaneBufferGeometry(planeWidth, band_width, w - 1, 1);
    const geom_ba = geom_fr.clone();
    const verticesFr = geom_fr.attributes.position.array;
    const verticesBa = geom_ba.attributes.position.array;

    let i; let
      mesh;
    for (i = 0; i < w; i += 1) {
      verticesFr[i * 3 + 1] = gridValues[k + i];
      verticesBa[i * 3 + 1] = gridValues[w - 1 - i];
    }
    mesh = new THREE.Mesh(geom_fr, material);
    mesh.rotation.x = Math.PI / 2;
    mesh.position.y = -planeHeight / 2;
    mesh.name = 'side';
    parent.add(mesh);

    mesh = new THREE.Mesh(geom_ba, material);
    mesh.rotation.x = Math.PI / 2;
    mesh.rotation.y = Math.PI;
    mesh.position.y = planeHeight / 2;
    mesh.name = 'side';
    parent.add(mesh);

    // left and right
    const geom_le = new THREE.PlaneBufferGeometry(band_width, planeHeight, 1, h - 1);


    const geomRi = geom_le.clone();

    const verticesLe = geom_le.attributes.position.array;


    const verticesRi = geomRi.attributes.position.array;

    for (i = 0; i < h; i += 1) {
      verticesLe[(i * 2 + 1) * 3] = gridValues[w * i];
      verticesRi[i * 2 * 3] = -gridValues[w * (i + 1) - 1];
    }
    mesh = new THREE.Mesh(geom_le, material);
    mesh.rotation.y = -Math.PI / 2;
    mesh.position.x = -planeWidth / 2;
    mesh.name = 'side';
    parent.add(mesh);

    mesh = new THREE.Mesh(geomRi, material);
    mesh.rotation.y = Math.PI / 2;
    mesh.position.x = planeWidth / 2;
    mesh.name = 'side';
    parent.add(mesh);

    // bottom
    let geom;
    if (Q3D.Config.exportMode) {
      geom = new THREE.PlaneBufferGeometry(planeWidth, planeHeight, w - 1, h - 1);
    } else {
      geom = new THREE.PlaneBufferGeometry(planeWidth, planeHeight, 1, 1);
    }
    mesh = new THREE.Mesh(geom, material);
    mesh.rotation.x = Math.PI;
    mesh.position.z = e0;
    mesh.name = 'bottom';
    parent.add(mesh);

    parent.updateMatrixWorld();
  },

  buildFrame(layer, parent, z0) {
    const opacity = (this.material.origProp.o !== undefined) ? this.material.origProp.o : 1;
    const material = new THREE.LineBasicMaterial({
      color: Q3D.Config.dem.frame.color,
      opacity,
      transparent: (opacity < 1),
    });
    layer.materials.add(material);

    const { grid } = this.data;
    const planeWidth = this.data.width;
    const planeHeight = this.data.height;

    // horizontal rectangle at bottom
    const hw = planeWidth / 2;
    const hh = planeHeight / 2;
    const e0 = z0 / this.data.zScale - this.data.zShift - (this.data.zShiftA || 0);
    const geom = new THREE.Geometry();

    geom.vertices.push(
      new THREE.Vector3(-hw, -hh, e0),
      new THREE.Vector3(hw, -hh, e0),
      new THREE.Vector3(hw, hh, e0),
      new THREE.Vector3(-hw, hh, e0),
      new THREE.Vector3(-hw, -hh, e0),
    );

    const obj = new THREE.Line(geom, material);
    obj.name = 'frame';
    parent.add(obj);

    // vertical lines at corners
    const pts = [[-hw, -hh, grid.array[grid.array.length - grid.width]],
      [hw, -hh, grid.array[grid.array.length - 1]],
      [hw, hh, grid.array[grid.width - 1]],
      [-hw, hh, grid.array[0]]];
    pts.forEach((pt) => {
      const _geom = new THREE.Geometry();
      _geom.vertices.push(new THREE.Vector3(pt[0], pt[1], pt[2]),
        new THREE.Vector3(pt[0], pt[1], e0));

      const _obj = new THREE.Line(_geom, material);

      _obj.name = 'frame';
      parent.add(_obj);
    });

    parent.updateMatrixWorld();
  },

  getValue(x, y) {
    const { grid } = this.data;

    if (x >= 0 && x < grid.width && y >= 0 && y < grid.height) {
      return grid.array[x + grid.width * y];
    }

    return null;
  },

  contains(x, y) {
    const { translate } = this.data;
    const xmin = translate[0] - this.data.width / 2;
    const xmax = translate[0] + this.data.width / 2;
    const ymin = translate[1] - this.data.height / 2;
    const ymax = translate[1] + this.data.height / 2;

    if (xmin <= x && x <= xmax && ymin <= y && y <= ymax) return true;
    return false;
  },

};


/*
Q3D.ClippedDEMBlock
*/
Q3D.ClippedDEMBlock = function ClippedDEMBlock() {};

Q3D.ClippedDEMBlock.prototype = {

  constructor: Q3D.ClippedDEMBlock,

  loadJSONObject(obj, layer, callback) {
    const { grid } = obj;
    this.data = obj;

    // load material
    this.material = new Q3D.Material();
    this.material.loadJSONObject(obj.material, () => {
      layer.requestRender();
    });
    layer.materials.add(this.material);

    const mesh = new THREE.Mesh(new THREE.Geometry(), this.material.mtl);
    mesh.position.fromArray(obj.translate);
    mesh.scale.z = obj.zScale;

    const buildGeometry = () => {
      mesh.geometry = Q3D.Utils.createOverlayGeometry(
        obj.clip.triangles, obj.clip.split_polygons, layer.getZ.bind(layer),
      );

      // set UVs
      Q3D.Utils.setGeometryUVs(mesh.geometry, layer.sceneData.width, layer.sceneData.height);

      if (callback) callback(mesh);
    };

    if (grid.url !== undefined) {
      Q3D.Application.loadFile(grid.url, 'arraybuffer', (buf) => {
        grid.array = new Float32Array(buf);
        buildGeometry(grid.array);
      });
    } else { // WebKit Bridge
      if (grid.binary !== undefined) {
        grid.array = new Float32Array(grid.binary.buffer, 0, grid.width * grid.height);
      }

      buildGeometry(grid.array);
    }

    this.obj = mesh;
    return mesh;
  },

  buildSides(layer, parent, z0) {
    const opacity = (this.material.origProp.o !== undefined) ? this.material.origProp.o : 1;
    const material = new THREE.MeshLambertMaterial({
      color: Q3D.Config.dem.side.color,
      opacity,
      transparent: (opacity < 1),
    });
    layer.materials.add(material);

    const { polygons } = this.data.clip;
    const e0 = z0 / this.data.zScale - this.data.zShift - (this.data.zShiftA || 0);
    const zFunc = layer.getZ.bind(layer);
    const bzFunc = () => e0;

    // make back-side material for bottom
    const matBack = material.clone();
    matBack.side = THREE.BackSide;
    layer.materials.add(matBack);

    let geom; let mesh; let shape; let
      vertices;
    for (let i = 0, l = polygons.length; i < l; i += 1) {
      const bnds = polygons[i];

      // sides
      for (let j = 0, m = bnds.length; j < m; j += 1) {
        // vertex order is counter clockwise (when boundary is outer)
        vertices = layer.segmentizeLineString(bnds[j].reverse(), zFunc);
        geom = Q3D.Utils.createWallGeometry(vertices, bzFunc);
        mesh = new THREE.Mesh(geom, material);
        mesh.name = 'side';
        parent.add(mesh);
      }

      // bottom
      shape = new THREE.Shape(Q3D.Utils.arrayToVec2Array(bnds[0]));
      for (let j = 1, m = bnds.length; j < m; j += 1) {
        shape.holes.push(new THREE.Path(Q3D.Utils.arrayToVec2Array(bnds[j])));
      }
      geom = new THREE.ShapeBufferGeometry(shape);
      mesh = new THREE.Mesh(geom, matBack);
      mesh.position.z = e0;
      mesh.name = 'bottom';
      parent.add(mesh);
    }
    parent.updateMatrixWorld();
  },

  getValue(x, y) {
    const { grid } = this.data;

    if (x >= 0 && x < grid.width && y >= 0 && y < grid.height) {
      return grid.array[x + grid.width * y];
    }

    return null;
  },

  contains(x, y) {
    const { translate } = this.data;
    const xmin = translate[0] - this.data.width / 2;
    const xmax = translate[0] + this.data.width / 2;
    const ymin = translate[1] - this.data.height / 2;
    const ymax = translate[1] + this.data.height / 2;

    if (xmin <= x && x <= xmax && ymin <= y && y <= ymax) return true;
    return false;
  },

};

/*
Q3D.MapLayer
*/
Q3D.MapLayer = function MapLayer() {
  this.queryable = true;

  this.materials = new Q3D.Materials();
  this.materials.addEventListener('renderRequest', this.requestRender.bind(this));

  this.objectGroup = new Q3D.Group();
  this.queryObjs = [];
};

Q3D.MapLayer.prototype = Object.create(THREE.EventDispatcher.prototype);
Q3D.MapLayer.prototype.constructor = Q3D.MapLayer;

Q3D.MapLayer.prototype.addObject = function addObject(object, queryable) {
  if (queryable === undefined) {
    ({ queryable } = this);
  }

  object.userData.layerId = this.id;
  this.objectGroup.add(object);

  if (queryable) {
    const { queryObjs } = this;

    object.traverse((obj) => {
      queryObjs.push(obj);
    });
  }
  return this.objectGroup.children.length - 1;
};

Q3D.MapLayer.prototype.queryableObjects = function queryableObjects() {
  return (this.visible) ? this.queryObjs : [];
};

Q3D.MapLayer.prototype.removeAllObjects = function removeAllObjects() {
  // dispose of geometries
  this.objectGroup.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
  });

  // dispose of materials
  this.materials.dispose();

  // remove all child objects from object group
  for (let i = this.objectGroup.children.length - 1; i >= 0; i -= 1) {
    this.objectGroup.remove(this.objectGroup.children[i]);
  }
  this.queryObjs = [];
};

Q3D.MapLayer.prototype.loadJSONObject = function loadJSONObject(jsonObject, scene) {
  if (jsonObject.type === 'layer') {
    // properties
    if (jsonObject.properties !== undefined) {
      this.properties = jsonObject.properties;
      this.visible = !!((jsonObject.properties.visible || Q3D.Config.allVisible));
    }

    if (jsonObject.data !== undefined) {
      this.removeAllObjects();

      // materials
      if (jsonObject.data.materials !== undefined) {
        this.materials.loadJSONObject(jsonObject.data.materials);
      }
    }

    this.sceneData = scene.userData;
    this._bbox = undefined;
  }
};

Object.defineProperty(Q3D.MapLayer.prototype, 'opacity', {
  get() {
    return this.materials.opacity();
  },
  set(value) {
    this.materials.setOpacity(value);
    this.requestRender();
  },
});

Object.defineProperty(Q3D.MapLayer.prototype, 'visible', {
  get() {
    return this.objectGroup.visible;
  },
  set(value) {
    this.objectGroup.visible = value;
    this.requestRender();
  },
});

Q3D.MapLayer.prototype.boundingBox = function boundingBox(forceUpdate) {
  if (!this._bbox || forceUpdate) {
    this._bbox = new THREE.Box3().setFromObject(this.objectGroup);
  }
  return this._bbox;
};

Q3D.MapLayer.prototype.setWireframeMode = function setWireframeMode(wireframe) {
  this.materials.setWireframeMode(wireframe);
};

Q3D.MapLayer.prototype.requestRender = function requestRender() {
  this.dispatchEvent({ type: 'renderRequest' });
};


/*
Q3D.DEMLayer --> Q3D.MapLayer
*/
Q3D.DEMLayer = function DEMLayer() {
  Q3D.MapLayer.call(this);
  this.type = Q3D.LayerType.DEM;
  this.blocks = [];
};

Q3D.DEMLayer.prototype = Object.create(Q3D.MapLayer.prototype);
Q3D.DEMLayer.prototype.constructor = Q3D.DEMLayer;

Q3D.DEMLayer.prototype.loadJSONObject = function loadJSONObject(jsonObject, scene) {
  Q3D.MapLayer.prototype.loadJSONObject.call(this, jsonObject, scene);
  if (jsonObject.type === 'layer') {
    if (jsonObject.data !== undefined) {
      jsonObject.data.forEach((obj) => {
        this.buildBlock(obj, scene);
      }, this);
    }
  } else if (jsonObject.type === 'block') {
    this.buildBlock(jsonObject, scene);
  }
};

Q3D.DEMLayer.prototype.buildBlock = function buildBlock(jsonObject, scene) {
  const index = jsonObject.block;
  this.blocks[index] = (jsonObject.clip === undefined)
    ? (new Q3D.DEMBlock()) : (new Q3D.ClippedDEMBlock());

  const mesh = this.blocks[index].loadJSONObject(jsonObject, this, (m) => {
    if (jsonObject.sides || jsonObject.frame) {
      this.sideVisible = true;

      const buildSides = () => {
        // build sides, bottom and frame
        if (jsonObject.sides) {
          this.blocks[index].buildSides(this, m, Q3D.Config.dem.side.bottomZ);
        }
        if (jsonObject.frame) {
          this.blocks[index].buildFrame(this, m, Q3D.Config.dem.frame.bottomZ);
        }
        this.requestRender();
      };

      if (Q3D.Config.autoZShift) {
        const listener = (event) => {
          // set adjusted z shift to every block
          this.blocks.forEach((block) => {
            block.data.zShiftA = event.sceneData.zShiftA;
          });
          buildSides();
          scene.removeEventListener('zShiftAdjusted', listener);
        };

        scene.addEventListener('zShiftAdjusted', listener);
      } else {
        buildSides();
      }
    }
    this.requestRender();
  });
  this.addObject(mesh);
};

// calculate elevation at the coordinates (x, y) on triangle face
Q3D.DEMLayer.prototype.getZ = function getZ(x, y) {
  for (let i = 0, l = this.blocks.length; i < l; i += 1) {
    const block = this.blocks[i];
    const { data } = block;

    if (!block.contains(x, y)) continue;

    const ix = data.width / (data.grid.width - 1);
    const iy = data.height / (data.grid.height - 1);
    const xmin = data.translate[0] - data.width / 2;
    const ymax = data.translate[1] + data.height / 2;
    const mx0 = Math.floor((x - xmin) / ix);
    const my0 = Math.floor((ymax - y) / iy);
    const z = [
      block.getValue(mx0, my0),
      block.getValue(mx0 + 1, my0),
      block.getValue(mx0, my0 + 1),
      block.getValue(mx0 + 1, my0 + 1),
    ];
    const px0 = xmin + ix * mx0;
    const py0 = ymax - iy * my0;
    const sdx = (x - px0) / ix;
    const sdy = (py0 - y) / iy;

    if (sdx <= 1 - sdy) return z[0] + (z[1] - z[0]) * sdx + (z[2] - z[0]) * sdy;
    return z[3] + (z[2] - z[3]) * (1 - sdx) + (z[1] - z[3]) * (1 - sdy);
  }
  return null;
};

Q3D.DEMLayer.prototype.segmentizeLineString = function segmentizeLineString(lineString, zFunc) {
  // does not support multiple blocks
  if (zFunc === undefined) zFunc = () => 0;
  const { width, height } = this.sceneData;
  const xmin = -width / 2;
  const ymax = height / 2;
  const { grid } = this.blocks[0].data;
  const ix = width / (grid.width - 1);
  const iy = height / (grid.height - 1);
  const sortFunc = (a, b) => a - b;
  const pts = [];

  for (let i = 1, l = lineString.length; i < l; i += 1) {
    const pt1 = lineString[i - 1]; const
      pt2 = lineString[i];
    const x1 = pt1[0];
    const x2 = pt2[0];
    const y1 = pt1[1];
    const y2 = pt2[1];
    const z1 = pt1[2];
    const z2 = pt2[2];
    const nx1 = (x1 - xmin) / ix;
    const nx2 = (x2 - xmin) / ix;
    const ny1 = (ymax - y1) / iy;
    const ny2 = (ymax - y2) / iy;
    const ns1 = Math.abs(ny1 + nx1);
    const ns2 = Math.abs(ny2 + nx2);
    const p = [0];
    const nvp = [[nx1, nx2], [ny1, ny2], [ns1, ns2]];

    for (let j = 0; j < 3; j += 1) {
      const v1 = nvp[j][0];
      const v2 = nvp[j][1];

      if (v1 === v2) continue;

      let k = Math.ceil(Math.min(v1, v2));
      const n = Math.floor(Math.max(v1, v2));

      for (; k <= n; k += 1) {
        p.push((k - v1) / (v2 - v1));
      }
    }

    p.sort(sortFunc);

    let x; let y; let z; let
      lp = null;
    for (let j = 0, m = p.length; j < m; j += 1) {
      if (lp === p[j]) continue;
      if (p[j] === 1) break;

      x = x1 + (x2 - x1) * p[j];
      y = y1 + (y2 - y1) * p[j];

      if (z1 === undefined || z2 === undefined) z = zFunc(x, y);
      else z = z1 + (z2 - z1) * p[j];

      pts.push(new THREE.Vector3(x, y, z));

      lp = p[j];
    }
  }
  // last point (= the first point)
  const pt = lineString[lineString.length - 1];
  pts.push(new THREE.Vector3(pt[0], pt[1], (pt[2] === undefined) ? zFunc(pt[0], pt[1]) : pt[2]));

  return pts;
};

Q3D.DEMLayer.prototype.setSideVisible = function setSideVisible(visible) {
  this.sideVisible = visible;
  this.objectGroup.traverse((obj) => {
    if (obj.name === 'side' || obj.name === 'bottom' || obj.name === 'frame') obj.visible = visible;
  });
};


/*
Q3D.VectorLayer --> Q3D.MapLayer
*/
Q3D.VectorLayer = function VectorLayer() {
  Q3D.MapLayer.call(this);
};

Q3D.VectorLayer.prototype = Object.create(Q3D.MapLayer.prototype);
Q3D.VectorLayer.prototype.constructor = Q3D.VectorLayer;

Q3D.VectorLayer.prototype.build = () => {};

Q3D.VectorLayer.prototype.loadJSONObject = function loadJSONObject(jsonObject, scene) {
  Q3D.MapLayer.prototype.loadJSONObject.call(this, jsonObject, scene);

  if (jsonObject.type === 'layer') {
    if (jsonObject.data !== undefined) {
      (jsonObject.data.blocks || []).forEach((block) => {
        if (block.url !== undefined) {
          Q3D.Application.loadJSONFile(scene, block.url);
        } else {
          this.build(block.features);
        }
      }, this);
    }
  } else if (jsonObject.type === 'block') {
    this.build(jsonObject.features);
  }
};

Object.defineProperty(Q3D.VectorLayer.prototype, 'visible', {
  get() {
    return Object.getOwnPropertyDescriptor(Q3D.MapLayer.prototype, 'visible').get.call(this);
  },
  set(value) {
    Object.getOwnPropertyDescriptor(Q3D.MapLayer.prototype, 'visible').set.call(this, value);
  },
});


/*
Q3D.PointLayer --> Q3D.VectorLayer
*/
Q3D.PointLayer = function PointLayer() {
  Q3D.VectorLayer.call(this);
  this.type = Q3D.LayerType.Point;
};

Q3D.PointLayer.prototype = Object.create(Q3D.VectorLayer.prototype);
Q3D.PointLayer.prototype.constructor = Q3D.PointLayer;

Q3D.PointLayer.prototype.loadJSONObject = function loadJSONObject(jsonObject, scene) {
  Q3D.VectorLayer.prototype.loadJSONObject.call(this, jsonObject, scene);
  if (jsonObject.type === 'layer' && jsonObject.properties.objType === 'Model File' && jsonObject.data !== undefined) {
    if (this.models === undefined) {
      this.models = new Q3D.Models();
      this.models.addEventListener('modelLoaded', (event) => {
        this.materials.addFromObject3D(event.model.scene);
        this.requestRender();
      });
    } else {
      this.models.clear();
    }
    this.models.loadJSONObject(jsonObject.data.models);
  }
};

Q3D.PointLayer.prototype.build = function build(features) {
  const { objType } = this.properties;

  if (objType === 'Point') { this.buildPoints(features); return; }
  if (objType === 'Icon') { this.buildIcons(features); return; }
  if (objType === 'Model File') { this.buildModels(features); return; }

  const deg2rad = Math.PI / 180;
  const rx = 90 * deg2rad;
  let setSR;
  let unitGeom;

  if (this.cachedGeometryType === objType) {
    unitGeom = this.geometryCache;
  }

  if (objType === 'Sphere') {
    setSR = (mesh, geom) => {
      mesh.scale.set(geom.r, geom.r, geom.r);
    };
    unitGeom = unitGeom || new THREE.SphereBufferGeometry(1, 32, 32);
  } else if (objType === 'Box') {
    setSR = (mesh, geom) => {
      mesh.scale.set(geom.w, geom.h, geom.d);
      mesh.rotation.x = rx;
    };
    unitGeom = unitGeom || new THREE.BoxBufferGeometry(1, 1, 1);
  } else if (objType === 'Disk') {
    const sz = this.sceneData.zExaggeration; // set 1 if not to be elongated

    setSR = (mesh, geom) => {
      mesh.scale.set(geom.r, geom.r * sz, 1);
      mesh.rotateOnWorldAxis(Q3D.uv.i, -geom.d * deg2rad);
      mesh.rotateOnWorldAxis(Q3D.uv.k, -geom.dd * deg2rad);
    };
    unitGeom = unitGeom || new THREE.CircleBufferGeometry(1, 32);
  } else if (objType === 'Plane') {
    const sz = this.sceneData.zExaggeration; // set 1 if not to be elongated

    setSR = (mesh, geom) => {
      mesh.scale.set(geom.w, geom.l * sz, 1);
      mesh.rotateOnWorldAxis(Q3D.uv.i, -geom.d * deg2rad);
      mesh.rotateOnWorldAxis(Q3D.uv.k, -geom.dd * deg2rad);
    };
    unitGeom = unitGeom || new THREE.PlaneBufferGeometry(1, 1, 1, 1);
  } else { // Cylinder or Cone
    setSR = (mesh, geom) => {
      mesh.scale.set(geom.r, geom.h, geom.r);
      mesh.rotation.x = rx;
    };
    unitGeom = unitGeom || ((objType === 'Cylinder') ? new THREE.CylinderBufferGeometry(1, 1, 1, 32) : new THREE.CylinderBufferGeometry(0, 1, 1, 32));
  }

  // iteration for features
  const { materials } = this;
  let f; let geom; let zAddend; let i; let l; let mesh; let pt;

  for (let fidx = 0, flen = features.length; fidx < flen; fidx += 1) {
    f = features[fidx];
    f.objIndices = [];

    ({ geom } = f);
    zAddend = (geom.h) ? geom.h / 2 : 0;
    for (i = 0, l = geom.pts.length; i < l; i += 1) {
      mesh = new THREE.Mesh(unitGeom, materials.mtl(f.mtl));
      setSR(mesh, geom);

      pt = geom.pts[i];
      mesh.position.set(pt[0], pt[1], pt[2] + zAddend);
      mesh.userData.properties = f.prop;

      f.objIndices.push(this.addObject(mesh));
    }
  }

  this.geometryCache = unitGeom;
  this.cachedGeometryType = objType;
};

Q3D.PointLayer.prototype.buildPoints = function buildPoints(features) {
  let f; let geom; let
    obj;
  for (let fidx = 0, flen = features.length; fidx < flen; fidx += 1) {
    f = features[fidx];

    geom = new THREE.BufferGeometry();
    geom.addAttribute('position',
      new THREE.BufferAttribute(new Float32Array(f.geom.pts), 3));

    obj = new THREE.Points(geom, this.materials.mtl(f.mtl));
    obj.userData.properties = f.prop;

    f.objIndices = [this.addObject(obj)];
  }
};

Q3D.PointLayer.prototype.buildIcons = function buildIcons(features) {
  // each feature in this layer
  features.forEach((f) => {
    let sprite;
    const objs = [];
    const material = this.materials.get(f.mtl);

    f.objIndices = [];
    for (let i = 0, l = f.geom.pts.length; i < l; i += 1) {
      sprite = new THREE.Sprite(material.mtl);
      sprite.position.fromArray(f.geom.pts[i]);
      sprite.userData.properties = f.prop;

      objs.push(sprite);
      f.objIndices.push(this.addObject(sprite));
    }

    material.callbackOnLoad(() => {
      const img = material.mtl.map.image;
      for (let i = 0; i < objs.length; i += 1) {
        // base size is 64 x 64
        objs[i].scale.set(img.width / 64 * f.geom.scale,
          img.height / 64 * f.geom.scale,
          1);
        objs[i].updateMatrixWorld();
      }
    });
  }, this);
};

Q3D.PointLayer.prototype.buildModels = function buildModels(features) {
  const q = new THREE.Quaternion();
  const e = new THREE.Euler();
  const deg2rad = Math.PI / 180;

  // each feature in this layer
  features.forEach((f) => {
    const model = this.models.get(f.model);

    f.objIndices = [];
    f.geom.pts.forEach((pt) => {
      model.callbackOnLoad((m) => {
        const obj = m.scene.clone();
        obj.scale.set(f.geom.scale, f.geom.scale, f.geom.scale);

        if (obj.rotation.x) {
          // reset coordinate system to z-up and specified rotation
          obj.rotation.set(0, 0, 0);
          obj.quaternion.multiply(q.setFromEuler(e.set(f.geom.rotateX * deg2rad,
            f.geom.rotateY * deg2rad,
            f.geom.rotateZ * deg2rad)));
        } else {
          // y-up to z-up and specified rotation
          obj.quaternion.multiply(q.setFromEuler(e.set(f.geom.rotateX * deg2rad,
            f.geom.rotateY * deg2rad,
            f.geom.rotateZ * deg2rad)));
          obj.quaternion.multiply(q.setFromEuler(e.set(Math.PI / 2, 0, 0)));
        }

        const parent = new THREE.Group();
        parent.scale.set(1, 1, this.sceneData.zExaggeration);
        parent.position.fromArray(pt);
        parent.userData.properties = f.prop;
        parent.add(obj);

        f.objIndices.push(this.addObject(parent));
      });
    });
  });
};

/*
Q3D.LineLayer --> Q3D.VectorLayer
*/
Q3D.LineLayer = function LineLayer() {
  Q3D.VectorLayer.call(this);
  this.type = Q3D.LayerType.Line;
};

Q3D.LineLayer.prototype = Object.create(Q3D.VectorLayer.prototype);
Q3D.LineLayer.prototype.constructor = Q3D.LineLayer;

Q3D.LineLayer.prototype.loadJSONObject = function loadJSONObject(jsonObject, scene) {
  Q3D.VectorLayer.prototype.loadJSONObject.call(this, jsonObject, scene);
};

Q3D.LineLayer.prototype.build = function build(features) {
  let createObject;
  const { objType } = this.properties;
  const { sceneData, materials } = this;

  if (objType === this._lastObjType && this._createObject !== undefined) {
    createObject = this._createObject;
  } else if (objType === 'Line') {
    createObject = (f, line) => {
      let pt; const
        positions = [];
      for (let i = 0, l = line.length; i < l; i += 1) {
        pt = line[i];
        positions.push(pt[0], pt[1], pt[2]);
      }
      const geom = new THREE.BufferGeometry();
      geom.addAttribute('position', new THREE.Float32BufferAttribute(positions, 3));

      const obj = new THREE.Line(geom, materials.mtl(f.mtl));
      if (obj.material instanceof THREE.LineDashedMaterial) obj.computeLineDistances();
      return obj;
    };
  } else if (objType === 'Pipe' || objType === 'Cone') {
    let jointGeom; let
      cylinGeom;
    if (objType === 'Pipe') {
      jointGeom = new THREE.SphereBufferGeometry(1, 32, 32);
      cylinGeom = new THREE.CylinderBufferGeometry(1, 1, 1, 32);
    } else {
      cylinGeom = new THREE.CylinderBufferGeometry(0, 1, 1, 32);
    }

    let mesh;
    const pt0 = new THREE.Vector3();
    const pt1 = new THREE.Vector3();
    const sub = new THREE.Vector3();
    const axis = Q3D.uv.j;

    createObject = (f, line) => {
      const group = new Q3D.Group();

      pt0.set(line[0][0], line[0][1], line[0][2]);
      for (let i = 1, l = line.length; i < l; i += 1) {
        pt1.set(line[i][0], line[i][1], line[i][2]);

        mesh = new THREE.Mesh(cylinGeom, materials.mtl(f.mtl));
        mesh.scale.set(f.geom.r, pt0.distanceTo(pt1), f.geom.r);
        mesh.position.set((pt0.x + pt1.x) / 2, (pt0.y + pt1.y) / 2, (pt0.z + pt1.z) / 2);
        mesh.quaternion.setFromUnitVectors(axis, sub.subVectors(pt1, pt0).normalize());
        group.add(mesh);

        if (jointGeom && i < l - 1) {
          mesh = new THREE.Mesh(jointGeom, materials.mtl(f.mtl));
          mesh.scale.set(f.geom.r, f.geom.r, f.geom.r);
          mesh.position.copy(pt1);
          group.add(mesh);
        }

        pt0.copy(pt1);
      }
      return group;
    };
  } else if (objType === 'Box') {
    // In this method, box corners are exposed near joint when both azimuth and slope of
    // the segments of both sides are different. Also, some unnecessary faces are created.
    const { debugMode } = Q3D.Config;
    const faces = [];

    const vi = [
      [0, 5, 4], [4, 5, 1], // left turn - top, side, bottom
      [3, 0, 7], [7, 0, 4],
      [6, 3, 2], [2, 3, 7],
      [4, 1, 0], [0, 1, 5], // right turn - top, side, bottom
      [1, 2, 5], [5, 2, 6],
      [2, 7, 6], [6, 7, 3],
    ];

    for (let j = 0; j < 12; j += 1) {
      faces.push(new THREE.Face3(vi[j][0], vi[j][1], vi[j][2]));
    }

    createObject = (f, line) => {
      const geometry = new THREE.Geometry();
      const group = new Q3D.Group(); // used in debug mode

      let geom; let mesh; let dist; let rx; let rz; let wh4; let vb4; let
        vf4;
      const pt0 = new THREE.Vector3();
      const pt1 = new THREE.Vector3();
      const sub = new THREE.Vector3();

      const pt = new THREE.Vector3();
      const ptM = new THREE.Vector3();
      const scale1 = new THREE.Vector3(1, 1, 1);
      const matrix = new THREE.Matrix4();
      const quat = new THREE.Quaternion();

      pt0.set(line[0][0], line[0][1], line[0][2]);

      for (let i = 1, l = line.length; i < l; i += 1) {
        pt1.set(line[i][0], line[i][1], line[i][2]);
        dist = pt0.distanceTo(pt1);
        sub.subVectors(pt1, pt0);
        rx = Math.atan2(sub.z, Math.sqrt(sub.x * sub.x + sub.y * sub.y));
        rz = Math.atan2(sub.y, sub.x) - Math.PI / 2;
        ptM.set((pt0.x + pt1.x) / 2, (pt0.y + pt1.y) / 2, (pt0.z + pt1.z) / 2); // midpoint
        quat.setFromEuler(new THREE.Euler(rx, 0, rz, 'ZXY'));
        matrix.compose(ptM, quat, scale1);

        // place a box to the segment
        geom = new THREE.BoxGeometry(f.geom.w, dist, f.geom.h);
        if (debugMode) {
          mesh = new THREE.Mesh(geom, materials.mtl(f.mtl));
          mesh.position.set(ptM.x, ptM.y, ptM.z);
          mesh.rotation.set(rx, 0, rz, 'ZXY');
          group.add(mesh);
        } else {
          geom.applyMatrix(matrix);
          geometry.merge(geom);
        }

        // joint
        // 4 vertices of backward side of current segment
        wh4 = [[-f.geom.w / 2, f.geom.h / 2],
          [f.geom.w / 2, f.geom.h / 2],
          [f.geom.w / 2, -f.geom.h / 2],
          [-f.geom.w / 2, -f.geom.h / 2]];
        vb4 = [];
        for (let j = 0; j < 4; j += 1) {
          pt.set(wh4[j][0], -dist / 2, wh4[j][1]);
          pt.applyMatrix4(matrix);
          vb4.push(pt.clone());
        }

        if (vf4) {
          geom = new THREE.Geometry();
          geom.vertices = vf4.concat(vb4);
          geom.faces = faces;
          if (debugMode) {
            geom.computeFaceNormals();
            group.add(new THREE.Mesh(geom));
          } else {
            geometry.merge(geom);
          }
        }

        // 4 vertices of forward side
        vf4 = [];
        for (let j = 0; j < 4; j += 1) {
          pt.set(wh4[j][0], dist / 2, wh4[j][1]);
          pt.applyMatrix4(matrix);
          vf4.push(new THREE.Vector3(pt.x, pt.y, pt.z));
        }

        pt0.copy(pt1);
      }

      if (debugMode) return group;

      geometry.mergeVertices();
      geometry.computeFaceNormals();
      return new THREE.Mesh(geometry, materials.mtl(f.mtl));
    };
  } else if (objType === 'Profile') {
    const z0 = sceneData.zShift * sceneData.zScale;

    createObject = (f, line) => {
      let pt;
      const vertices = [];
      for (let i = 0, l = line.length; i < l; i += 1) {
        pt = line[i];
        vertices.push(new THREE.Vector3(pt[0], pt[1], pt[2]));
      }

      const bzFunc = () => z0 + f.geom.bh;

      return new THREE.Mesh(Q3D.Utils.createWallGeometry(vertices, bzFunc),
        materials.mtl(f.mtl));
    };
  }

  // each feature in this layer
  let f; let i; let l; let
    obj;
  for (let fidx = 0, flen = features.length; fidx < flen; fidx += 1) {
    f = features[fidx];
    f.objIndices = [];

    for (i = 0, l = f.geom.lines.length; i < l; i += 1) {
      obj = createObject(f, f.geom.lines[i]);
      obj.userData.properties = f.prop;

      f.objIndices.push(this.addObject(obj));
    }
  }

  this._lastObjType = objType;
  this._createObject = createObject;
};


/*
Q3D.PolygonLayer --> Q3D.VectorLayer
*/
Q3D.PolygonLayer = function PolygonLayer() {
  Q3D.VectorLayer.call(this);
  this.type = Q3D.LayerType.Polygon;

  // for overlay
  this.borderVisible = true;
  this.sideVisible = true;
};

Q3D.PolygonLayer.prototype = Object.create(Q3D.VectorLayer.prototype);
Q3D.PolygonLayer.prototype.constructor = Q3D.PolygonLayer;

Q3D.PolygonLayer.prototype.loadJSONObject = function loadJSONObject(jsonObject, scene) {
  Q3D.VectorLayer.prototype.loadJSONObject.call(this, jsonObject, scene);
};

Q3D.PolygonLayer.prototype.build = function build(features) {
  let createObject;
  const { sceneData, materials } = this;

  if (this.properties.objType === this._lastObjType && this._createObject !== undefined) {
    createObject = this._createObject;
  } else if (this.properties.objType === 'Extruded') {
    const createSubObject = function createSubObject(f, polygon, z) {
      let i; let l;

      const shape = new THREE.Shape(Q3D.Utils.arrayToVec2Array(polygon[0]));
      for (i = 1, l = polygon.length; i < l; i += 1) {
        shape.holes.push(new THREE.Path(Q3D.Utils.arrayToVec2Array(polygon[i])));
      }

      // extruded geometry
      let geom = new THREE.ExtrudeBufferGeometry(shape, { bevelEnabled: false, amount: f.geom.h });
      const mesh = new THREE.Mesh(geom, materials.mtl(f.mtl.face));
      mesh.position.z = z;

      if (f.mtl.border !== undefined) {
        // border
        let border; let pt; let pts;
        const zFunc = () => 0;

        for (i = 0, l = polygon.length; i < l; i += 1) {
          pts = Q3D.Utils.arrayToVec3Array(polygon[i], zFunc);

          geom = new THREE.Geometry();
          geom.vertices = pts;

          border = new THREE.Line(geom, materials.mtl(f.mtl.border));
          mesh.add(border);

          border = new THREE.Line(geom, materials.mtl(f.mtl.border));
          border.position.z = f.geom.h;
          mesh.add(border);

          // vertical lines
          for (let j = 0, m = geom.vertices.length - 1; j < m; j += 1) {
            pt = pts[j];

            geom = new THREE.Geometry();
            geom.vertices.push(pt, new THREE.Vector3(pt.x, pt.y, pt.z + f.geom.h));
            border = new THREE.Line(geom, materials.mtl(f.mtl.border));
            mesh.add(border);
          }
        }
      }
      return mesh;
    };

    createObject = (f) => {
      if (f.geom.polygons.length === 1) {
        return createSubObject(f, f.geom.polygons[0], f.geom.centroids[0][2]);
      }

      const group = new THREE.Group();
      for (let i = 0, l = f.geom.polygons.length; i < l; i += 1) {
        group.add(createSubObject(f, f.geom.polygons[i], f.geom.centroids[i][2]));
      }
      return group;
    };
  } else if (this.properties.objType === 'Overlay') {
    const z0 = sceneData.zShift * sceneData.zScale;

    createObject = (f) => {
      let polygons;
      let zFunc;

      if (f.geom.polygons) {
        ({ polygons } = f.geom);
        zFunc = () => z0 + f.geom.h;
      } else {
        polygons = f.geom.split_polygons || []; // with z values
      }

      const geom = Q3D.Utils.createOverlayGeometry(f.geom.triangles, polygons, zFunc);
      return new THREE.Mesh(geom, materials.mtl(f.mtl));

      // TODO: [Polygon - Overlay] border
    };
  } else {
    createObject = (f) => {
      const vertices = f.geom.v;


      const indices = f.geom.f;

      const geom = new THREE.Geometry();

      for (let i = 0, l = vertices.length; i < l; i += 3) {
        geom.vertices.push(
          new THREE.Vector3(vertices[i], vertices[i + 1], vertices[i + 2]),
        );
      }

      for (let i = 0, l = indices.length; i < l; i += 3) {
        geom.faces.push(
          new THREE.Face3(indices[i], indices[i + 1], indices[i + 2]),
        );
      }

      geom.computeFaceNormals();

      return new THREE.Mesh(geom, materials.mtl(f.mtl));
    };
  }

  // each feature in this layer
  let f; let
    obj;
  for (let i = 0, l = features.length; i < l; i += 1) {
    f = features[i];
    obj = createObject(f);
    obj.userData.properties = f.prop;

    f.objIndices = [this.addObject(obj)];
  }

  this._lastObjType = this.properties.objType;
  this._createObject = createObject;
};

Q3D.PolygonLayer.prototype.setBorderVisible = function setBorderVisible(visible) {
  if (this.properties.objType !== 'Overlay') return;

  this.objectGroup.children.forEach((parent) => {
    for (let i = 0, l = parent.children.length; i < l; i += 1) {
      const obj = parent.children[i];
      if (obj instanceof THREE.Line) obj.visible = visible;
    }
  });
  this.borderVisible = visible;
};

Q3D.PolygonLayer.prototype.setSideVisible = function setSideVisible(visible) {
  if (this.properties.objType !== 'Overlay') return;

  this.objectGroup.children.forEach((parent) => {
    for (let i = 0, l = parent.children.length; i < l; i += 1) {
      const obj = parent.children[i];
      if (obj instanceof THREE.Mesh) obj.visible = visible;
    }
  });
  this.sideVisible = visible;
};

/*
Q3D.Models
*/
Q3D.Models = function Models() {
  this.models = [];
  this.cache = {};
};

Q3D.Models.prototype = Object.create(THREE.EventDispatcher.prototype);
Q3D.Models.prototype.constructor = Q3D.Models;

Q3D.Models.prototype.loadJSONObject = function loadJSONObject(jsonObject) {
  const callback = (model) => {
    this.dispatchEvent({ type: 'modelLoaded', model });
  };

  let model;
  let url;

  for (let i = 0, l = jsonObject.length; i < l; i += 1) {
    ({ url } = jsonObject[i].url);

    if (this.cache[url] !== undefined) {
      model = this.cache[url];
    } else {
      model = new Q3D.Model();
      model.load(url, callback);
      this.cache[url] = model;
    }

    this.models.push(model);
  }
};

Q3D.Models.prototype.get = function get(index) {
  return this.models[index];
};

Q3D.Models.prototype.clear = function clear() {
  this.models = [];
};


// Q3D.Utils - Utilities
Q3D.Utils = {};

// convert latitude and longitude in degrees to the following format
// Ndd°mm′ss.ss″, Eddd°mm′ss.ss″
Q3D.Utils.convertToDMS = function convertToDMS(lat, lon) {
  function toDMS(degrees) {
    const deg = Math.floor(degrees);


    const m = (degrees - deg) * 60;


    const min = Math.floor(m);


    const sec = (m - min) * 60;
    return `${deg}°${(`0${min}`).slice(-2)}′${(sec < 10) ? '0' : ''}${sec.toFixed(2)}″`;
  }

  return `${((lat < 0) ? 'S' : 'N') + toDMS(Math.abs(lat))}, ${
    (lon < 0) ? 'W' : 'E'}${toDMS(Math.abs(lon))}`;
};

Q3D.Utils.createWallGeometry = function createWallGeometry(vertices, bzFunc) {
  const geom = new THREE.Geometry();


  let pt = vertices[0];
  geom.vertices.push(
    new THREE.Vector3(pt.x, pt.y, pt.z),
    new THREE.Vector3(pt.x, pt.y, bzFunc(pt.x, pt.y)),
  );

  for (let i = 1, i2 = 1, l = vertices.length; i < l; i += 1, i2 += 2) {
    pt = vertices[i];
    geom.vertices.push(
      new THREE.Vector3(pt.x, pt.y, pt.z),
      new THREE.Vector3(pt.x, pt.y, bzFunc(pt.x, pt.y)),
    );

    geom.faces.push(
      new THREE.Face3(i2 - 1, i2, i2 + 1),
      new THREE.Face3(i2 + 1, i2, i2 + 2),
    );
  }
  geom.computeFaceNormals();
  return geom;
};

Q3D.Utils.arrayToVec2Array = function arrayToVec2Array(points) {
  let pt; const
    pts = [];
  for (let i = 0, l = points.length; i < l; i += 1) {
    pt = points[i];
    pts.push(new THREE.Vector2(pt[0], pt[1]));
  }
  return pts;
};

Q3D.Utils.arrayToVec3Array = function arrayToVec3Array(points, zFunc) {
  let pt; const
    pts = [];
  if (zFunc === undefined) {
    for (let i = 0, l = points.length; i < l; i += 1) {
      pt = points[i];
      pts.push(new THREE.Vector3(pt[0], pt[1], pt[2]));
    }
  } else {
    for (let i = 0, l = points.length; i < l; i += 1) {
      pt = points[i];
      pts.push(new THREE.Vector3(pt[0], pt[1], zFunc(pt[0], pt[1])));
    }
  }
  return pts;
};

Q3D.Utils.arrayToFace3Array = function arrayToFace3Array(faces) {
  let f; const
    fs = [];
  for (let i = 0, l = faces.length; i < l; i += 1) {
    f = faces[i];
    fs.push(new THREE.Face3(f[0], f[1], f[2]));
  }
  return fs;
};

Q3D.Utils.createOverlayGeometry = function createOverlayGeometry(triangles, polygons, zFunc) {
  const geom = new THREE.Geometry();

  // vertices and faces
  if (triangles !== undefined) {
    geom.vertices = Q3D.Utils.arrayToVec3Array(triangles.v, zFunc);
    geom.faces = Q3D.Utils.arrayToFace3Array(triangles.f);
  }

  // split-polygons
  for (let i = 0, l = polygons.length; i < l; i += 1) {
    const polygon = polygons[i];
    const polyGeom = new THREE.Geometry();


    const holes = [];

    // make Vector3 arrays
    polyGeom.vertices = Q3D.Utils.arrayToVec3Array(polygon[0], zFunc);
    for (let j = 1, m = polygon.length; j < m; j += 1) {
      holes.push(Q3D.Utils.arrayToVec3Array(polygon[j], zFunc));
    }

    // triangulate polygon
    const faces = THREE.ShapeUtils.triangulateShape(polyGeom.vertices, holes);

    // append points of holes to vertices
    for (let j = 0, m = holes.length; j < m; j += 1) {
      Array.prototype.push.apply(polyGeom.vertices, holes[j]);
    }

    // element of faces is [index1, index2, index3]
    polyGeom.faces = Q3D.Utils.arrayToFace3Array(faces);

    geom.merge(polyGeom);
  }
  geom.mergeVertices();
  geom.computeFaceNormals();
  geom.computeVertexNormals();
  return geom;
};

Q3D.Utils.setGeometryUVs = function setGeometryUVs(geom, baseWidth, baseHeight) {
  let face; let v; const
    uvs = [];

  for (let i = 0, l = geom.vertices.length; i < l; i += 1) {
    v = geom.vertices[i];
    uvs.push(new THREE.Vector2(v.x / baseWidth + 0.5, v.y / baseHeight + 0.5));
  }

  // eslint-disable-next-line
  geom.faceVertexUvs[0] = [];

  for (let i = 0, l = geom.faces.length; i < l; i += 1) {
    face = geom.faces[i];
    geom.faceVertexUvs[0].push([uvs[face.a], uvs[face.b], uvs[face.c]]);
  }
};

export default Q3D;
